import React from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { exportToCSV } from '../../../services/exportService';

const ResourceDrivers = ({ onShowEvidence }) => {
  const resourceMetrics = {
    activeCount: 342,
    avgProductivity: 87.5,
    utilization: 78.3,
    avgCost: 125000
  };

  const productivityData = [
  { month: "Aug 2025", productivity: 82.5 },
  { month: "Sep 2025", productivity: 84.2 },
  { month: "Oct 2025", productivity: 86.8 },
  { month: "Nov 2025", productivity: 85.9 },
  { month: "Dec 2025", productivity: 87.1 },
  { month: "Jan 2026", productivity: 87.5 }];


  const utilizationByDept = [
  { department: "Engineering", utilization: 85.2, resources: 120 },
  { department: "Sales", utilization: 78.5, resources: 65 },
  { department: "Operations", utilization: 82.1, resources: 85 },
  { department: "Support", utilization: 71.3, resources: 42 },
  { department: "Marketing", utilization: 68.9, resources: 30 }];


  const highPerformers = [
  {
    name: "Sarah Chen",
    department: "Engineering",
    productivity: 96.5,
    projects: 12,
    rating: 5.0,
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_133eb4ed9-1763296612935.png",
    avatarAlt: "Professional Asian woman with long black hair in business attire smiling confidently"
  },
  {
    name: "Michael Rodriguez",
    department: "Sales",
    productivity: 94.8,
    projects: 18,
    rating: 4.9,
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1f54e9b24-1763295596551.png",
    avatarAlt: "Hispanic man with short dark hair in navy suit with warm professional smile"
  },
  {
    name: "Emily Watson",
    department: "Operations",
    productivity: 93.2,
    projects: 15,
    rating: 4.9,
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1135cd9d1-1763298257986.png",
    avatarAlt: "Caucasian woman with blonde hair in professional blazer with friendly expression"
  },
  {
    name: "David Kim",
    department: "Engineering",
    productivity: 92.7,
    projects: 14,
    rating: 4.8,
    avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_114bd845f-1763298899395.png",
    avatarAlt: "Asian man with glasses and short black hair in business casual attire"
  }];


  const lowPerformers = [
  {
    name: "John Smith",
    department: "Support",
    productivity: 58.3,
    issues: "Frequent absences and missed deadlines",
    action: "Performance improvement plan initiated",
    status: "Under Review"
  },
  {
    name: "Lisa Anderson",
    department: "Marketing",
    productivity: 62.1,
    issues: "Low engagement and quality concerns",
    action: "Additional training and mentorship assigned",
    status: "In Progress"
  },
  {
    name: "Robert Taylor",
    department: "Operations",
    productivity: 64.8,
    issues: "Skill gaps in new technology stack",
    action: "Enrolled in upskilling program",
    status: "In Progress"
  }];


  const workloadPressure = [
  {
    category: "Overloaded",
    count: 45,
    percentage: 13.2,
    avgHours: 58,
    risk: "High",
    description: "Resources working significantly above capacity with burnout risk"
  },
  {
    category: "Optimal",
    count: 245,
    percentage: 71.6,
    avgHours: 42,
    risk: "Low",
    description: "Resources operating at healthy capacity with good work-life balance"
  },
  {
    category: "Underused",
    count: 52,
    percentage: 15.2,
    avgHours: 28,
    risk: "Medium",
    description: "Resources with available capacity for additional assignments"
  }];


  const costPerResource = [
  { role: "Senior Engineer", cost: 185000, count: 45 },
  { role: "Engineer", cost: 125000, count: 75 },
  { role: "Sales Manager", cost: 145000, count: 25 },
  { role: "Sales Rep", cost: 95000, count: 40 },
  { role: "Operations Lead", cost: 135000, count: 30 }];


  const utilizationData = [
  { department: "Engineering", utilization: 87, capacity: 120, active: 104 },
  { department: "Sales", utilization: 92, capacity: 85, active: 78 },
  { department: "Operations", utilization: 78, capacity: 95, active: 74 },
  { department: "Marketing", utilization: 81, capacity: 45, active: 36 },
  { department: "Support", utilization: 89, capacity: 65, active: 58 }];


  const getRiskColor = (risk) => {
    if (risk === 'High') return 'var(--color-error)';
    if (risk === 'Medium') return 'var(--color-warning)';
    return 'var(--color-success)';
  };

  const handleExportCSV = () => {
    const resourceData = utilizationData?.map((item) => ({
      Department: item?.department,
      'Utilization %': item?.utilization,
      Capacity: item?.capacity,
      'Active Resources': item?.active
    }));

    exportToCSV({
      title: 'Resource_Utilization',
      data: resourceData
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-md bg-primary/10">
            <Icon name="Users" size={20} color="var(--color-primary)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">People & Resources</h2>
            <p className="text-sm text-muted-foreground font-caption">Workforce analytics and resource utilization</p>
          </div>
        </div>
        <Button
          onClick={handleExportCSV}
          variant="outline"
          size="sm"
          iconName="Download">
          
          Export CSV
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Active Resources</span>
            <Icon name="UserCheck" size={18} color="var(--color-primary)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">{resourceMetrics?.activeCount}</p>
          <p className="text-xs text-success font-caption mt-1">+8 vs last month</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Avg Productivity</span>
            <Icon name="TrendingUp" size={18} color="var(--color-success)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">{resourceMetrics?.avgProductivity}%</p>
          <p className="text-xs text-success font-caption mt-1">+2.3% improvement</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Utilization Rate</span>
            <Icon name="Activity" size={18} color="var(--color-secondary)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">{resourceMetrics?.utilization}%</p>
          <p className="text-xs text-muted-foreground font-caption mt-1">Target: 75-85%</p>
        </div>

        <div className="bg-card rounded-lg border border-border p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground font-caption">Avg Cost/Resource</span>
            <Icon name="DollarSign" size={18} color="var(--color-accent)" />
          </div>
          <p className="text-2xl md:text-3xl font-heading font-semibold text-foreground font-data">${(resourceMetrics?.avgCost / 1000)?.toFixed(0)}K</p>
          <p className="text-xs text-warning font-caption mt-1">+3.2% vs last year</p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Productivity Trend</h3>
          <div className="h-64 md:h-80" aria-label="Productivity Trend Line Chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={productivityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  dataKey="month"
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }} />
                
                <YAxis
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                  domain={[75, 95]}
                  tickFormatter={(value) => `${value}%`} />
                
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-popover)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`${value}%`, 'Productivity']} />
                
                <Line
                  type="monotone"
                  dataKey="productivity"
                  stroke="var(--color-success)"
                  strokeWidth={3}
                  dot={{ fill: 'var(--color-success)', r: 5 }} />
                
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Resource Utilization by Department</h3>
          <div className="h-64 md:h-80" aria-label="Utilization by Department Bar Chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={utilizationByDept} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis
                  type="number"
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                  tickFormatter={(value) => `${value}%`} />
                
                <YAxis
                  type="category"
                  dataKey="department"
                  tick={{ fill: 'var(--color-muted-foreground)', fontSize: 12 }}
                  width={100} />
                
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--color-popover)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px'
                  }}
                  formatter={(value) => [`${value}%`, 'Utilization']} />
                
                <Bar dataKey="utilization" fill="var(--color-secondary)" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">High Performers</h3>
          <div className="space-y-4">
            {highPerformers?.map((performer, index) =>
            <div key={index} className="flex items-center gap-4 p-3 rounded-md bg-success/5 border border-success/20 hover:bg-success/10 transition-smooth">
                <img
                src={performer?.avatar}
                alt={performer?.avatarAlt}
                className="w-12 h-12 rounded-full object-cover flex-shrink-0" />
              
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="text-sm font-semibold text-foreground">{performer?.name}</h4>
                    <div className="flex items-center gap-1">
                      <Icon name="Star" size={14} color="var(--color-warning)" fill="var(--color-warning)" />
                      <span className="text-xs font-semibold text-foreground font-data">{performer?.rating}</span>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground font-caption mb-1">{performer?.department}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground font-caption">
                    <span>{performer?.productivity}% productivity</span>
                    <span>•</span>
                    <span>{performer?.projects} projects</span>
                  </div>
                </div>
                <Icon name="Award" size={20} color="var(--color-success)" />
              </div>
            )}
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border p-4 md:p-6">
          <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Low Performers</h3>
          <div className="space-y-4">
            {lowPerformers?.map((performer, index) =>
            <div key={index} className="p-4 rounded-md border border-warning/30 bg-warning/5">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="text-sm font-semibold text-foreground mb-1">{performer?.name}</h4>
                    <p className="text-xs text-muted-foreground font-caption mb-1">{performer?.department}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-warning font-data">{performer?.productivity}% productivity</span>
                      <span className="px-2 py-0.5 rounded text-xs font-semibold bg-warning/20 text-warning font-caption">
                        {performer?.status}
                      </span>
                    </div>
                  </div>
                  <Icon name="AlertCircle" size={20} color="var(--color-warning)" />
                </div>
                <p className="text-xs text-muted-foreground mb-2 font-caption">{performer?.issues}</p>
                <div className="flex items-start gap-2 p-2 rounded bg-background">
                  <Icon name="Target" size={14} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-foreground font-caption">{performer?.action}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Workload Pressure Indicator</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {workloadPressure?.map((category, index) =>
          <div
            key={index}
            className="p-4 rounded-md border"
            style={{
              backgroundColor: `${getRiskColor(category?.risk)}10`,
              borderColor: `${getRiskColor(category?.risk)}30`
            }}>
            
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-foreground">{category?.category}</h4>
                <span
                className="px-2 py-1 rounded text-xs font-semibold font-caption"
                style={{
                  backgroundColor: `${getRiskColor(category?.risk)}20`,
                  color: getRiskColor(category?.risk)
                }}>
                
                  {category?.risk} Risk
                </span>
              </div>
              <div className="space-y-2 mb-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground font-caption">Resources</span>
                  <span className="text-sm font-semibold text-foreground font-data">{category?.count}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground font-caption">Percentage</span>
                  <span className="text-sm font-semibold text-foreground font-data">{category?.percentage}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground font-caption">Avg Hours/Week</span>
                  <span className="text-sm font-semibold text-foreground font-data">{category?.avgHours}h</span>
                </div>
              </div>
              <p className="text-xs text-muted-foreground font-caption">{category?.description}</p>
            </div>
          )}
        </div>
      </div>
      <div className="bg-card rounded-lg border border-border p-4 md:p-6">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">Cost per Resource by Role</h3>
        <div className="space-y-4">
          {costPerResource?.map((role, index) =>
          <div key={index} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-foreground">{role?.role}</span>
                  <span className="text-xs text-muted-foreground font-caption">({role?.count} resources)</span>
                </div>
                <span className="text-sm font-semibold text-foreground font-data">${(role?.cost / 1000)?.toFixed(0)}K</span>
              </div>
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                className="h-full bg-accent rounded-full transition-smooth"
                style={{ width: `${role?.cost / 185000 * 100}%` }} />
              
              </div>
            </div>
          )}
        </div>
      </div>
    </div>);

};

export default ResourceDrivers;